export type CommunityProfile = {
  slug: string;
  name: string;
  kind: "county" | "city";
  parent?: string;
  tagline: string;
  hero_image_url?: string;
  overview: string;
  history: string;
  culture: string;
  aspirations: string[];
  quick_facts: { label: string; value: string }[];
  // Used to match ideas/legislation/partners
  match: {
    counties: string[]; // matches affiliates.counties and legislation.county
    keywords: string[]; // matches idea title/description/tags (case-insensitive)
  };
};

export const COMMUNITY_PROFILES: CommunityProfile[] = [
  {
    slug: "mobile-county",
    name: "Mobile County",
    kind: "county",
    tagline: "Alabama's port-of-entry county — Gulf-shaped, working-class, and quietly inventing its next economy.",
    overview:
      "Mobile County anchors Alabama's coastal economy: a deepwater port, aerospace and shipbuilding hubs, and the state's third-largest city. It's a place where industrial scale and neighborhood-scale community work co-exist — and where the platform's pilot work is rooted.",
    history:
      "Founded in 1812, Mobile County grew around the Port of Mobile and the river systems feeding it. It carries deep French, Spanish, African, and Creole influence — the original home of American Mardi Gras (1703, predating New Orleans). Its 20th-century arc bent through shipbuilding booms, civil-rights struggles, and the long shadow of segregation-era disinvestment in north Mobile and Prichard. The 2020s are reframing the region around aerospace (Airbus), logistics, and a slowly diversifying small-business base.",
    culture:
      "Mobile County's identity is plural: Gulf Coast Creole, African-American working class, Vietnamese fishing communities along the bay, and a deeply Catholic civic tradition. Mardi Gras, jubilee festivals, and church-anchored neighborhood life still set the social calendar. Cuisine and music carry the same layered DNA — seafood, soul, and brass.",
    aspirations: [
      "A local small-business ecosystem that captures more of the port and aerospace supply chain.",
      "Coordinated revitalization of historically disinvested corridors (Prichard, North Mobile, Africatown).",
      "Resilience planning that takes hurricane exposure, sea-level rise, and stormwater seriously.",
      "A measurable pipeline of community-led ideas → funded projects → published lessons.",
    ],
    quick_facts: [
      { label: "Population", value: "≈ 411,000" },
      { label: "County seat", value: "Mobile" },
      { label: "Major industries", value: "Port logistics, aerospace, shipbuilding, healthcare" },
      { label: "Median home value (2024)", value: "$178,300" },
      { label: "Pilot status", value: "Active EMRKT pilot county" },
    ],
    match: {
      counties: ["Mobile", "Mobile County"],
      keywords: ["mobile county", "mobile, al", "africatown", "tillmans corner", "theodore", "saraland", "chickasaw", "bayou la batre"],
    },
  },
  {
    slug: "prichard",
    name: "Prichard",
    kind: "city",
    parent: "Mobile County",
    tagline: "A historic Black-majority city rebuilding civic infrastructure from the inside out.",
    overview:
      "Prichard sits just north of Mobile, a city of roughly 19,000 with deep cultural weight and a long fiscal climb. It's a place where small wins compound — a renovated mall, a stabilized water system, a school re-accredited — and where community ideas have the shortest distance to actual change.",
    history:
      "Incorporated in 1925, Prichard grew quickly through the mid-20th century as a working-class suburb of Mobile, peaking at ~47,000 residents by 1970. Population and tax base contracted sharply after deindustrialization and white flight; the city has spent decades managing the financial overhang, including high-profile pension and water-utility crises. The 2020s are a deliberate reset: new leadership, federal infrastructure dollars, and visible civic-anchor projects like the redevelopment of the Prichard Mall.",
    culture:
      "Prichard is one of Alabama's most consistently Black-majority cities (≈85%), with a civic culture built on churches, civil-rights legacy, and family lineages that span generations in the same neighborhoods. It produced a remarkable number of national figures relative to its size — athletes, ministers, and policymakers — and the community remembers its own.",
    aspirations: [
      "Reclaim the Prichard Mall site as a community anchor (retail, services, jobs).",
      "Stabilize core utilities (water, sewer, drainage) so private investment can underwrite itself.",
      "Grow a community-led small-business corridor along Main Street and Wilson Avenue.",
      "Capture every Prichard project as a documented case study so other Alabama cities can borrow what works.",
    ],
    quick_facts: [
      { label: "Population", value: "≈ 19,300" },
      { label: "County", value: "Mobile County" },
      { label: "Demographics", value: "≈ 85% Black / African-American" },
      { label: "Median household income", value: "≈ $30,400" },
      { label: "Pilot status", value: "First EMRKT featured community" },
    ],
    match: {
      counties: ["Mobile", "Mobile County"],
      keywords: ["prichard", "prichard mall"],
    },
  },
];

export const getCommunityProfile = (slug: string) =>
  COMMUNITY_PROFILES.find((c) => c.slug === slug) ?? null;